
import { Apis } from '@/apiManager/Apis';
import _requestHelper from '@/apiManager/_requestHelper';
import Product from '@/models/Product';
import ItemType from './ItemType.vue';
import MoneyFormat from "@/components/UI/Money/UIMoneyFormat.vue";
import ItemUnitMeasure from './ItemComponents/ItemUnitMeasure.vue';
import ItemsSliderWithTitle from './ItemsSliderWithTitle.vue';
import CartActions from "@/components/Cart/CartActions/CartActions.vue";
import ServiceQuantitySelector from './ItemComponents/ServiceQuantitySelector.vue';
import Vue from 'vue'
import { mapMutations, mapState } from 'vuex'
import Breadcrumbs from '../UI/Breadcrumbs.vue';
import InitLoader from '../UI/Loader/InitLoader.vue';
import EmptyView from '../Cart/EmptyCart.vue';
import UITitle from '../UI/Text/UITitle.vue';
import { logMetaPixelEvent } from '@/metaPixel/LogMetaPixelEvent';
import FirebaseAnalyticsScreensNames from '@/firebase/firebaseAnalyticsScreensNames';
import { trackItemDetailsPage } from '@/adjustTracking/adjustService';
import { isRudderStack } from '@/analytics/analyticsMode';
import StoreSettings from '@/models/StoreSettings';
import { EProductOfferingType } from '@/enums/EProductOfferingType';
import StoreService from '@/services/storeService';
import {
    buildCanonicalUrl,
    buildBreadcrumbItemsFromRoute,
    buildBreadcrumbListJsonLd,
    buildProductJsonLd,
    buildSeoMeta,
    buildSeoTitle,
    formatSeoText,
    getRuntimeSeoConfig,
    getSeoOrigin
} from '@/helpers/seoMetaHelper';
import { canUseSelectedStore, ensureSelectedStoreForRoute } from '@/helpers/ssrStorefrontHelper';
export default Vue.extend({

    components: {
        "item-type": ItemType,
        "money-format": MoneyFormat,
        "item-unit-measure": ItemUnitMeasure, 
        "items-slider-with-title": ItemsSliderWithTitle,
        "cart-actions": CartActions,
        "service-quantity-selector": ServiceQuantitySelector,
        "breadcrumbs": Breadcrumbs,
        "init-loader": InitLoader,
        "empty-view": EmptyView,
        "ui-title": UITitle
    },

    data: () => ({
        loading: false as boolean,
        itemInformation: [] as any,
        similarItems: [] as any[],
        alsoBoughtWithItems: [] as any[],
        relatedBundleItems: [] as any[],
        relatedBundles: [] as any[],
        itemsByBrand: [] as any[],
        selectedItem: {} as Product,
        selectedImage: "" as string,
        hiResImageFailedToLoad: false as boolean,
        lowResImageFailedToLoad: false as boolean,
        multiImageFailedToLoad: [] as boolean[],
        isEmptyViewDisplayed: false as boolean,
        serviceRowWidth: 0 as number,
    }),

    metaInfo: function() {
        const partnerSettings = (this as any).partnerSettings || {};
        const selectedStoreDetails = (this as any).selectedStoreDetails || {};
        const selectedItem = (this as any).selectedItem || {};
        const routeParams = this.$route.params || {};
        const vueAppConfig = getRuntimeSeoConfig();
        const origin = getSeoOrigin(this);

        const partnerName = partnerSettings.partnerName || vueAppConfig.VUE_APP_Title || "Online Store";
        const productName = selectedItem.name || formatSeoText(routeParams.productName) || "Product";
        const storeName = selectedStoreDetails.displayName || partnerName;
        const title = buildSeoTitle({
            primary: productName,
            siteName: storeName,
            descriptor: "Buy Online",
            city: selectedStoreDetails.address && selectedStoreDetails.address.city
        });
        const descriptionParts = [
            selectedItem.brandName,
            selectedItem.category,
            selectedItem.subCategory
        ].filter((value: string) => !!value);
        const description = descriptionParts.length > 0
            ? `Shop ${productName} online from ${storeName}. ${descriptionParts.join(" - ")}.`
            : `Shop ${productName} online from ${storeName}.`;
        const keywords = partnerSettings.seoKeywords || vueAppConfig.VUE_APP_Meta_Keywords || "";
        const itemImage = selectedItem.image || (selectedItem.images && selectedItem.images.length > 0 ? selectedItem.images[0] : "");
        const image = itemImage && partnerSettings.blobUrl
            ? `${partnerSettings.blobUrl}items/${itemImage}`
            : partnerSettings.seoImageUrl || partnerSettings.appBarLogoUrl || partnerSettings.logoUrl || "";
        const canonicalUrl = buildCanonicalUrl(this);
        const price = selectedItem.adjPriceValue || selectedItem.adjPrice || selectedItem.price;
        const currency = selectedStoreDetails.defaultCurrency
            ? selectedStoreDetails.defaultCurrency.currencyCode
            : "";
        const structuredData: any[] = [];
        const productJsonLd = buildProductJsonLd({
            name: productName,
            description,
            image,
            url: canonicalUrl,
            brand: selectedItem.brandName,
            price,
            currency,
            availability: selectedItem.isOutOfStock
                ? "https://schema.org/OutOfStock"
                : "https://schema.org/InStock"
        });
        const breadcrumbJsonLd = buildBreadcrumbListJsonLd(
            buildBreadcrumbItemsFromRoute(this.$route, this.$router, origin, this)
        );

        if (productJsonLd) {
            structuredData.push(productJsonLd);
        }

        if (breadcrumbJsonLd) {
            structuredData.push(breadcrumbJsonLd);
        }

        return buildSeoMeta({
            title,
            description,
            keywords,
            image,
            canonicalUrl,
            type: "product",
            structuredData
        });
    },

    computed: {
        ...mapState("ui", ["isMobileViewDisplayed"]),
        ...mapState("partner", ["partnerSettings", "appDataContent"]),
        ...mapState("store", ["selectedStoreDetails"]),
        ...mapState("product", ["currentSelectedItem"]),

        itemIdFromRoute(): any {
            return this.$route.params["productId"];
        },

        itemHasWeightRule(): boolean {
            return Product.hasWeightRule(this.selectedItem as Product);
        },

        serviceQuantitySuffix(): string {
            return Product.getServiceQuantitySuffix(this.selectedItem as Product);
        },

        isServiceStore(): boolean {
            return (this.selectedStoreDetails as StoreSettings).productOfferingType == EProductOfferingType.Service;
        },

        displayImages(): string[] {
            return Product.getDisplayImages(this.selectedItem as Product);
        }
    },


    methods: {
        ...mapMutations("firebase", ["LOG_FIREBASE_SCREEN", "LOG_FIREBASE_EVENT"]),
        ...mapMutations(["LOG_RS_SCREEN", "LOG_RS_EVENT"]),

        // Builds descriptive, SEO-friendly alt text for a product image: name + brand (when known)
        // plus an optional detail describing what this particular image shows (view number, thumbnail, etc.)
        getItemImageAlt(detail?: string): string {
            const item = this.selectedItem as Product;
            const name = item.name || "Product";
            const brand = (item as any).brandName;
            const base = brand ? `${name} by ${brand}` : name;

            return detail ? `${base} - ${detail}` : base;
        },

        async getGenericMoreItems() {
            await ensureSelectedStoreForRoute(this);

            if (!canUseSelectedStore(this)) {
                return;
            }

            this.$emit("loadingState", true);

            // this.selectedItem = {} as Product;
            this.itemInformation = [] as any;
            this.similarItems = [] as any[];
            this.alsoBoughtWithItems = [] as any[];
            this.relatedBundleItems = [] as any[];
            this.relatedBundles = [] as any[];

            this.loading = true;

            if(this.isServiceStore == true) {
                await this.getServiceItemDetails();

                this.loading = false;
                this.$emit("loadingState", false);
                return;
            }

            await _requestHelper({
                method: "POST",
                url: `${Apis.itemsSearchURL}/${typeof this.$route.params["productId"] == "undefined" ? this.currentSelectedItem.id : this.$route.params["productId"]}`,
                data: { 
                    id: Number.parseInt(typeof this.$route.params["productId"] == "undefined" ? this.currentSelectedItem.id : this.$route.params["productId"]),
                    includeItemDetails: true,
                    includeItemAttributes: true,
                    includeSimilarItemsByBrand: true,
                    includeSimilarItemsByType: true,
                    includeSimilarItemsAlsoBoughtWith: true,
                    branchId: this.selectedStoreDetails.id
                },
                loaderKey: "",
                successCallback: (result: any) => {

                    this.selectedItem = Product.fetchItemObjectFromJson(result.itemDetails);

                    if(typeof (this as any).selectedItem != "undefined") {
                        const images = Product.getDisplayImages((this as any).selectedItem);
                        this.selectedImage = images[0];
                        this.multiImageFailedToLoad = images.map( (_: any) => false)
                    }

                    else if(typeof this.currentSelectedItem != "undefined") {
                        const images = Product.getDisplayImages((this as any).currentSelectedItem);
                        this.selectedImage = images[0];
                        this.multiImageFailedToLoad = images.map( (_: any) => false)
                    }

                    // Related bundle items
                    if(result.relatedBundleItems != null && typeof(result.relatedBundleItems) != "undefined")
                        this.relatedBundleItems = result.relatedBundleItems.map((item: any) => Product.fetchItemObjectFromJson(item));
                    
                    // Related Bundles
                    if(result.relatedBundles != null && typeof(result.relatedBundles) != "undefined")
                        this.relatedBundles = result.relatedBundles.map((item: any) => Product.fetchItemObjectFromJson(item));
                    
                        // similar items
                    if(result.similarItemsByType)
                        this.similarItems = result.similarItemsByType.map((item: any) => Product.fetchItemObjectFromJson(item));
                    
                    // bought With
                    if(result.alsoBoughtWithItems)
                        this.alsoBoughtWithItems = result.alsoBoughtWithItems.map((item: any) => Product.fetchItemObjectFromJson(item));
            
                    // brand
                    if(result.similarItemsByBrand)
                        this.itemsByBrand = result.similarItemsByBrand.map((item: any) => Product.fetchItemObjectFromJson(item));

                    // item information
                    if(result.itemInformation)
                        this.itemInformation = result.itemInformation;

                        this.$store.commit("product/UPDATE_PRODUCT_DETAILS_LOADING_STATE", false);
                }
            }).catch(() => {
                this.isEmptyViewDisplayed = true
            });
            
            this.loading = false;
            this.$emit("loadingState", false);
        },

        // single-service stores: fetch the store's one item/service directly from the dedicated
        // branch-services/list endpoint (same one the mobile app uses), attributes included, instead of
        // the generic item-details fetch above (which has no notion of this store type)
        async getServiceItemDetails() {
            var storeDetails = this.selectedStoreDetails as StoreSettings;

            try {
                var item = await StoreService.getServiceItemForBranch(storeDetails.id, storeDetails.defaultCurrency.currencyCode);

                if(item == null) {
                    this.isEmptyViewDisplayed = true;
                    return;
                }

                this.selectedItem = item;
                const images = Product.getDisplayImages(item);
                this.selectedImage = images[0] ?? "";
                this.multiImageFailedToLoad = images.map(() => false);
                this.itemInformation = item.attributes ?? [];
            }

            catch(error) {
                this.isEmptyViewDisplayed = true;
            }
        },

        // pins the service-item wrapper's width to the image+info row's rendered width (see the
        // comment above that wrapper in the template) so expanding the Service Information panel,
        // or however long its text is, never changes the layout's width. Re-measured on resize
        // since the row's own width is responsive.
        measureServiceRowWidth() {
            const row = this.$refs.serviceRow as HTMLElement | undefined;
            this.serviceRowWidth = row?.offsetWidth ?? 0;
        }
    },

    async mounted() {
        await this.getGenericMoreItems()

        if (this.itemHasWeightRule == true) this.$nextTick(() => this.measureServiceRowWidth());
        window.addEventListener("resize", this.measureServiceRowWidth);

        if (isRudderStack()) {
            this.LOG_RS_SCREEN(FirebaseAnalyticsScreensNames.ItemDetails);
            this.LOG_RS_EVENT({
                event: "Product Viewed",
                params: {
                    product_id: this.selectedItem.id,
                    name: this.selectedItem.name,
                    price: (this.selectedItem as any).adjPriceValue,
                    currency: (this.selectedStoreDetails as StoreSettings).defaultCurrency
                }
            });
            this.LOG_RS_EVENT({
                event: "Product Clicked",
                params: {
                    product_id: this.selectedItem.id,
                    name: this.selectedItem.name
                }
            });
        }
        else {
            logMetaPixelEvent('view_item', {
                content_name: this.selectedItem.name
            });

        this.LOG_FIREBASE_SCREEN(FirebaseAnalyticsScreensNames.ItemDetails);
        this.LOG_FIREBASE_EVENT('view_item', {
            content_name: this.selectedItem.name
        });
        this.LOG_FIREBASE_EVENT('select_item', {
            content_name: this.selectedItem.name
        });

        trackItemDetailsPage((this as any).partnerSettings.adjustConfig)
            logMetaPixelEvent('select_item', {
                content_name: this.selectedItem.name
            });

            this.LOG_FIREBASE_SCREEN(FirebaseAnalyticsScreensNames.ItemDetails);
            this.LOG_FIREBASE_EVENT('view_item', {
                content_name: this.selectedItem.name
            });
            this.LOG_FIREBASE_EVENT('select_item', {
                content_name: this.selectedItem.name
            });
        }
    },

    beforeDestroy() {
        window.removeEventListener("resize", this.measureServiceRowWidth);
    },

    watch: {
        async "currentSelectedItem.id" () {
            // this.scrollOffset = 0;
            this.hiResImageFailedToLoad = false;
            this.lowResImageFailedToLoad = false;
            await this.getGenericMoreItems();

            if (this.itemHasWeightRule == true) this.$nextTick(() => this.measureServiceRowWidth());
        },

        async itemIdFromRoute (val) {
            this.hiResImageFailedToLoad = false;
            this.lowResImageFailedToLoad = false;
            await this.getGenericMoreItems();

            if (this.itemHasWeightRule == true) this.$nextTick(() => this.measureServiceRowWidth());
        },
        
        selectedImage() {
            this.hiResImageFailedToLoad = false;
            this.lowResImageFailedToLoad = false;
        }
    },
    
})
